import { useState } from 'react';

import {
  appBorders,
  appColors,
  lottieAnimations,
  BackrsIcon,
  Tier,
  TIERS,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  getMarkersOfSuccessStrings,
  shouldSchoolLevelBeFiltered,
} from '@backrs_ui/grow-ui/src/components/MarkersOfSuccess/markersOfSuccess.util';
import { MarkersOfSuccessExpandableGroup } from '@backrs_ui/grow-ui/src/components/MarkersOfSuccess/MarkersOfSuccessExpandableGroup';
import { SCHOOL_LEVEL, SchoolLevel } from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme, Typography, Menu, MenuItem, MenuProps } from '@mui/material';
import Lottie from 'lottie-react';

const FALSE_LOADING_TIME = 1000;

/**
 * At 1247px, we need to stack the tabs instead of have them in columns
 * the variable is here as a constant to make changing it easier
 */
const TAB_STACK_MEDIA_QUERY = 1247;

type TabbedContainerProps = Readonly<{
  tier: Tier;
  schoolLevel: SchoolLevel;
  handleTierChange: (tier: Tier) => void;
  handleSchoolLevelChange: (schoolLevel: SchoolLevel) => void;
}>;

export const TabbedContainer = ({
  tier,
  schoolLevel,
  handleTierChange,
  handleSchoolLevelChange,
}: TabbedContainerProps) => {
  const [tierMenuAnchorEl, setTierMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [schoolMenuAnchorEl, setSchoolMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [isFalseLoading, setIsFalseLoading] = useState(false);

  const handleTierTabClick = (event: React.MouseEvent<HTMLElement>) => {
    setTierMenuAnchorEl(event.currentTarget);
  };

  const handleSchoolTabClick = (event: React.MouseEvent<HTMLElement>) => {
    setSchoolMenuAnchorEl(event.currentTarget);
  };

  const handleTierChangeWithLoad = (tier: Tier) => {
    setIsFalseLoading(true);

    setTimeout(() => {
      setIsFalseLoading(false);
      handleTierChange(tier);
    }, FALSE_LOADING_TIME);
  };

  const handleSchoolLevelChangeWithLoad = (schoolLevel: SchoolLevel) => {
    setIsFalseLoading(true);

    setTimeout(() => {
      setIsFalseLoading(false);
      handleSchoolLevelChange(schoolLevel);
    }, FALSE_LOADING_TIME);
  };

  /** props used for both popovers */
  const commonPopoverProps: Omit<MenuProps, 'open' | 'anchorEl'> = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
    transformOrigin: {
      vertical: 'center',
      horizontal: 'right',
    },
    onClose: () => {
      setTierMenuAnchorEl(null);
      setSchoolMenuAnchorEl(null);
    },
    PaperProps: {
      sx: selectMenuPaperStyle,
    },
  } as const;

  return (
    <Box sx={containerStyle}>
      {/* TABS */}
      <Box sx={tabsContainerStyle}>
        <Box id="markers-tier-tab" sx={tabStyle} onClick={handleTierTabClick}>
          <Typography sx={tabTitleStyle}>{tier}</Typography>
          <Box
            sx={{
              ...downChevronContainerStyle,
              backgroundColor: tierMenuAnchorEl ? appColors.neutral.white : 'unset',
            }}
          >
            <BackrsIcon
              name="IconDownChevronCircle"
              alt="Apply"
              color={appColors.neutral.black}
              height={24}
              width={24}
            />
          </Box>
        </Box>
        <Box id="markers-school-level-tab" sx={schoolTabStyle} onClick={handleSchoolTabClick}>
          <Typography sx={tabTitleStyle}>{getMarkersOfSuccessStrings(schoolLevel)}</Typography>
          <Box
            sx={{
              ...downChevronContainerStyle,
              backgroundColor: schoolMenuAnchorEl ? appColors.neutral.white : 'unset',
            }}
          >
            <BackrsIcon
              name="IconDownChevronCircle"
              alt="Apply"
              color={appColors.neutral.black}
              height={24}
              width={24}
            />
          </Box>
        </Box>
      </Box>

      {/* MARKERS OF SUCCESS */}
      <Box sx={expandableSuccessItemsContainer}>
        {isFalseLoading && (
          <Box sx={loadingAnimationContainerStyle}>
            <Lottie animationData={lottieAnimations.dotsDotting} loop style={animationStyle} />
          </Box>
        )}

        <Box
          sx={{
            opacity: isFalseLoading ? 0 : 1,
          }}
        >
          <Typography sx={tierExplanationTextStyle}>
            {appStrings.modules.results.diagram[tier].tooltip}
          </Typography>
          <MarkersOfSuccessExpandableGroup
            tier={tier}
            schoolLevel={schoolLevel}
            // set key to force re-render when tier or schoolLevel changes
            key={`${tier}-${schoolLevel}-${isFalseLoading}`}
            expandedMarkersOfSuccessIndex={null}
          />
        </Box>
      </Box>

      {/* MENU ITEMS - CAREER TRACKS */}
      <Menu
        anchorEl={tierMenuAnchorEl}
        open={Boolean(tierMenuAnchorEl)}
        {...commonPopoverProps}
        PaperProps={{
          sx: { ...selectMenuPaperStyle, marginTop: 9 },
        }}
      >
        {Object.values(TIERS).map((tier) => {
          if (tier === TIERS.ENTREPRENEURSHIP || tier === TIERS.SEMISKILLED) {
            return null;
          }

          return (
            <MenuItem
              id={`markers-tier-option-${tier.toLowerCase().replace(/[\s/]+/g, '-')}`}
              key={tier}
              onClick={() => {
                handleTierChangeWithLoad(tier);
                setTierMenuAnchorEl(null);
              }}
              sx={menuItemStyle}
            >
              {tier}
            </MenuItem>
          );
        })}
      </Menu>

      {/* MENU ITEMS - EDUCATION LEVEL */}
      <Menu
        anchorEl={schoolMenuAnchorEl}
        open={Boolean(schoolMenuAnchorEl)}
        {...commonPopoverProps}
      >
        {Object.values(SCHOOL_LEVEL).map((schoolLevel) => {
          if (shouldSchoolLevelBeFiltered(schoolLevel)) {
            return null;
          }

          return (
            <MenuItem
              id={`markers-school-level-option-${schoolLevel.toLowerCase().replace(/[\s_]+/g, '-')}`}
              key={schoolLevel}
              onClick={() => {
                handleSchoolLevelChangeWithLoad(schoolLevel);
                setSchoolMenuAnchorEl(null);
              }}
              sx={menuItemStyle}
            >
              {getMarkersOfSuccessStrings(schoolLevel)}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  maxWidth: {
    xs: '100%',
    md: '42%',
  },
  alignSelf: 'flex-start',
} as const;

const loadingAnimationContainerStyle: SxProps<Theme> = {
  position: 'absolute',
  left: 'calc(50% - 40px)',
  top: 'calc(50% - 40px)',
  zIndex: 10,
} as const;

const selectMenuPaperStyle: SxProps<Theme> = {
  backgroundColor: appColors.neutral.black,
  color: appColors.neutral.white,
  borderBottomLeftRadius: '.5rem',
  borderBottomRightRadius: '.5rem',
  borderTopLeftRadius: '0',
  borderTopRightRadius: '0',
  marginTop: '36px',
} as const;

const menuItemStyle: SxProps<Theme> = {
  '&:hover': {
    opacity: 0.7,
  },
} as const;

const tabsContainerStyle: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  [`@media (min-width: ${TAB_STACK_MEDIA_QUERY}px)`]: {
    flexDirection: 'row',
    width: 'fit-content',
  },
  zIndex: 1,
  marginBottom: -1.25,
  width: '100%',
  boxSizing: 'border-box',
} as const;

const expandableSuccessItemsContainer: SxProps<Theme> = {
  position: 'relative',
  zIndex: 1,
  px: {
    xs: 2,
    sm: 4,
  },
  pb: {
    xs: 4,
    md: 8,
  },
  pt: 3,
  backgroundColor: appColors.neutral.white,
  border: `2px solid ${appColors.neutral.black}`,
  borderRadius: '.5rem',
} as const;

const tabStyle: SxProps<Theme> = {
  backgroundColor: appColors.color.blue1,
  width: '100%',
  minWidth: { sm: '256px' },
  paddingX: 2,
  paddingTop: 1,
  paddingBottom: 2,
  borderTopLeftRadius: '.5rem',
  borderTopRightRadius: '.5rem',
  border: appBorders.backrsBorders.black.border,
  borderWidth: 2,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,
  zIndex: 9,
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',
  top: 10,
  boxSizing: 'border-box',
  [`@media (min-width: ${TAB_STACK_MEDIA_QUERY}px)`]: {
    width: 'fit-content',
    zIndex: 10,
    top: 'unset',
  },
} as const;

const schoolTabStyle: SxProps<Theme> = {
  ...tabStyle,
  minWidth: { sm: '216px' },
  backgroundColor: appColors.color.blue2,
  position: 'relative',
  left: 0,
  top: 'unset',
  pl: 2,
  zIndex: 10,
  [`@media (min-width: ${TAB_STACK_MEDIA_QUERY}px)`]: {
    left: -12,
    top: 0,
    pl: 4,
    zIndex: 9,
  },
} as const;

const tierExplanationTextStyle: SxProps<Theme> = {
  fontSize: '16px',
  lineHeight: '1.3',
  mb: {
    xs: 3,
    md: 0,
  },
} as const;

const downChevronContainerStyle: SxProps<Theme> = {
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
} as const;

const tabTitleStyle: SxProps<Theme> = {
  fontWeight: 700,
  color: appColors.neutral.black,
  fontSize: '16px',
} as const;

const animationStyle: React.CSSProperties = {
  height: '80px',
  width: '80px',
} as const;
